import { useState, useCallback } from "react";

/**
 * Hook de géolocalisation RGPD-friendly (opt-in).
 * Appelle request() seulement quand l'utilisateur clique sur un bouton
 * explicite "Autour de moi".
 */
export function useGeolocation() {
  const [coords, setCoords] = useState(() => {
    try {
      const cached = sessionStorage.getItem("proxiRedGeo");
      if (cached) {
        const { lat, lng, ts } = JSON.parse(cached);
        // Cache 15 minutes
        if (Date.now() - ts < 15 * 60 * 1000) return { lat, lng };
      }
    } catch { /* ignore */ }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const request = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setError("La géolocalisation n'est pas disponible dans ce navigateur.");
        resolve(null); return;
      }
      setLoading(true); setError("");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(c);
          try { sessionStorage.setItem("proxiRedGeo", JSON.stringify({ ...c, ts: Date.now() })); } catch { /* ignore */ }
          setLoading(false);
          resolve(c);
        },
        (err) => {
          setError(err.code === 1
            ? "Autorisation refusée. Activez la géolocalisation dans votre navigateur pour utiliser cette fonction."
            : "Position indisponible pour le moment.");
          setLoading(false);
          resolve(null);
        },
        { enableHighAccuracy: false, maximumAge: 10 * 60 * 1000, timeout: 10000 }
      );
    });
  }, []);

  const clear = useCallback(() => {
    setCoords(null);
    try { sessionStorage.removeItem("proxiRedGeo"); } catch { /* ignore */ }
  }, []);

  // Définit une position manuelle (ex. issue d'un code postal géocodé).
  const setManual = useCallback((c) => {
    if (!c || c.lat == null || c.lng == null) return;
    const coords = { lat: c.lat, lng: c.lng };
    setCoords(coords);
    setError("");
    try { sessionStorage.setItem("proxiRedGeo", JSON.stringify({ ...coords, ts: Date.now() })); } catch { /* ignore */ }
  }, []);

  return { coords, loading, error, request, clear, setManual };
}
